import {
  Badge,
  InputSelectionList,
  InputSelectionListChangeEvent,
  SelectionList,
} from '@dixa/lib-shared-habla';
import Fuse from 'fuse.js';
import { SearchFilterConditionCustomAttributeSelect } from 'lib-search-types';
import { SearchFilterLayout } from 'lib-search-ui';
import intersection from 'lodash/intersection';
import isEqual from 'lodash/isEqual';
import { memo, useCallback, useMemo, useState } from 'react';
import { useDebounce } from 'react-use';

import {
  CustomFieldSelectAttribute,
  CustomFieldSelectAttributeOption,
} from 'apis/customFields';
import { CustomFieldDisplayValue } from 'components/customFields';
import { convertTreeToFlatItems } from 'utils/tree';

import { ConditionFieldProps } from '../../../../conditionField';

import styles from './conditionFieldCustomAttributeSelect.module.scss';

type ConditionFieldCustomAttributeSelectValue =
  SearchFilterConditionCustomAttributeSelect['values'][number]['value'];

export type ConditionFieldCustomAttributeSelectProps = {
  /** The custom field select attribute. */
  attribute: CustomFieldSelectAttribute;
} & ConditionFieldProps<
  SearchFilterConditionCustomAttributeSelect<'conversation_custom_attribute'>
>;

export const ConditionFieldCustomAttributeSelect = memo(
  function ConditionFieldCustomAttributeSelect({
    attribute,
    condition,
    placeholder,
    disabled,
    onChange,
  }: ConditionFieldCustomAttributeSelectProps) {
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

    useDebounce(() => setDebouncedSearchTerm(searchTerm), 300, [searchTerm]);

    const handleToggleDropdown = useCallback((isOpen: boolean) => {
      if (!isOpen) {
        setSearchTerm('');
      }
    }, []);

    /**
     * Handles changing the filter values.
     *
     * If there is at least one value selected, the filter is either added or
     * updated in the state. Otherwise if no values remain selected, the filter
     * is removed from the state. When selecting a node value, any existing
     * selected child values are automatically removed.
     */
    const handleChange = useCallback(
      ({
        selectedValues,
        selectedItem,
      }: InputSelectionListChangeEvent<ConditionFieldCustomAttributeSelectValue>) => {
        const newValues =
          selectedItem?.isNode && selectedItem?.isSelected
            ? selectedValues.filter((value) => {
                // A value is a child value of the selected node value if it is longer than the node
                // value and has the same first items as the node value.
                const isChildValue =
                  value.length > selectedItem.value.length &&
                  intersection(value, selectedItem.value).length > 0;

                return !isChildValue;
              })
            : selectedValues;

        onChange(newValues.map((value) => ({ label: '', value })));
      },
      [onChange],
    );

    /**
     * The flattened list of options, where each value is represented by a path
     * of all its parents' values. Used when searching, to select leaf values.
     */
    const flatOptions = useMemo(
      () =>
        convertTreeToFlatItems(attribute.options, {
          childrenKey: 'options',
          transformItem: (option, path) => ({
            ...option,
            label: path
              .map(({ label }) => label)
              .concat([option.label])
              .join(' / '),
            value: path.map(({ value }) => value).concat([option.value]),
          }),
        }).filter(({ options }) => !options?.length),
      [attribute.options],
    );

    /** Handles rendering of flat options when searching. */
    const renderFilteredListItems = useCallback(() => {
      const fuse = new Fuse(flatOptions, {
        keys: ['label', 'value'],
        threshold: 0.2,
        distance: 200,
      });

      const filteredOptions = fuse
        .search(debouncedSearchTerm)
        .map(({ item }) => item);

      if (!filteredOptions.length) {
        return <SearchFilterLayout.Empty />;
      }

      return filteredOptions.map((option) => (
        <SearchFilterLayout.Item
          key={option.id}
          value={option.value}
          label={option.label}
          highlight={debouncedSearchTerm}
        />
      ));
    }, [debouncedSearchTerm, flatOptions]);

    /**
     * Handles rendering a selection list item when the value is a leaf, and
     * when the value is a node and has nested child options, it renders another
     * list recursively.
     */
    const renderListOrItem = useCallback(
      (
        option: CustomFieldSelectAttributeOption,
        parentValuePath: ConditionFieldCustomAttributeSelectValue = [],
      ) => {
        const valuePath = [...parentValuePath, option.value];

        if (option.options?.length) {
          const isSelected = condition.values.some(({ value }) =>
            isEqual(value, valuePath),
          );
          const selectedChildValues = condition.values.filter(
            ({ value }) =>
              value.length > valuePath.length &&
              isEqual(value.slice(0, valuePath.length), valuePath),
          );

          const parentItem = (
            <SearchFilterLayout.Item
              key={option.id}
              value={valuePath}
              label={option.label}
              isDisabled={disabled}
              isIndeterminate={selectedChildValues.length > 0}
              contentClassName={styles.itemContent}
            >
              {selectedChildValues.length > 0 && (
                <Badge variant="secondary" size="small">
                  {selectedChildValues.length}
                </Badge>
              )}
            </SearchFilterLayout.Item>
          );

          // If the parent item is selected, don't render the children.
          if (isSelected) return parentItem;

          return (
            <SelectionList.List key={option.id} parentItem={parentItem}>
              {[...option.options].map((childOption) =>
                renderListOrItem(childOption, valuePath),
              )}
            </SelectionList.List>
          );
        }

        return (
          <SearchFilterLayout.Item
            key={option.id}
            value={valuePath}
            label={option.label}
            isDisabled={disabled}
          />
        );
      },
      [condition.values, disabled],
    );

    /** Handles rendering the selection list items recursively. */
    const renderListItems = useCallback(
      () => attribute.options.map((option) => renderListOrItem(option)),
      [attribute.options, renderListOrItem],
    );

    /** Returns the title of a selected value. */
    const getItemTitleByValue = useCallback(
      (
        value?: ConditionFieldCustomAttributeSelectValue | null,
      ): JSX.Element | null => {
        if (!value) return null;

        return (
          <CustomFieldDisplayValue
            customField={{
              attribute,
              value: {
                data_type: 'string_list',
                value,
              },
            }}
          />
        );
      },
      [attribute],
    );

    /** The current selection value. */
    const value = useMemo(
      () =>
        condition.values.map(
          ({ value }) => value as ConditionFieldCustomAttributeSelectValue,
        ),
      [condition.values],
    );

    return (
      <InputSelectionList
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        multiple
        clearable
        filter={{
          value: searchTerm,
          onChange: (e) => setSearchTerm(e.target.value),
        }}
        equalityFn={isEqual}
        getItemTitleByValue={getItemTitleByValue}
        onChange={handleChange}
        onToggleDropdown={handleToggleDropdown}
        dropdownProps={{ className: styles.dropdown }}
      >
        {debouncedSearchTerm ? renderFilteredListItems() : renderListItems()}
      </InputSelectionList>
    );
  },
);
